import { action, get, set } from '@ember/object';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import dayjs from 'dayjs';
import cloneDeep from 'lodash.clonedeep';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { refreshIfOtaIsDisconnected } from 'lumen/utils/disconnected-otas';
import { extendObj } from 'lumen/utils/extend-vanilla-js';
import {
  mapCompareParamsToAPIParams,
  mapRateParamToAPIParams,
} from 'lumen/utils/rates-request';
import { hash } from 'rsvp';

export const NUM_OF_DAYS = 7;

export default class MarketMarketratesRoute extends Route {
  queryParams = {
    brands: {
      refreshModel: true,
    },
    compareType: {
      refreshModel: true,
    },
    compareValue: {
      refreshModel: true,
    },
    day: {
      refreshModel: true,
    },
    expandedRowIds: {
      refreshModel: true,
    },
    los: {
      refreshModel: true,
    },
    managementTypes: {
      refreshModel: true,
    },
    maxPersons: {
      refreshModel: true,
    },
    mealPlan: {
      refreshModel: true,
    },
    membershipType: {
      refreshModel: true,
    },
    otaId: {
      refreshModel: true,
    },
    page: {
      refreshModel: true,
    },
    platform: {
      refreshModel: true,
    },
    rateParam: {
      refreshModel: true,
    },
    roomType: {
      refreshModel: true,
    },
    view: {
      refreshModel: false,
    },
  };

  @service access;
  @service alerting;
  @service('hotel-and-market') hotelAndMarketService;
  @service router;
  @service store;
  @service transition;

  addCompetitorsForRow = false;

  beforeModel(transition) {
    // ToDo: wouldn't it be better if this was done after the access/QP validation?
    // Only unload if we need to fetch all of the ratesummaries
    if (!this.addCompetitorsForRow) {
      this.store.unloadAll('ratesummary');
      this.store.unloadAll('rate');
    }

    if (!this.access.hasFeature('app.market.rates')) {
      this.transition.redirectToEnabledTab(transition);
    } else {
      this.transition.validateMarketRatesQueryParams(transition);
    }
  }

  fetchExpandedRows(model, params, requestDays) {
    const markets = {};
    model.ratesummaries.forEach((summary, index) => {
      markets[summary.market.id] = index;
    });
    params.expandedRowIds.forEach((id) => {
      const market = this.store.peekRecord('market', id);
      if (!isEmpty(market) && markets[id]) {
        const requestPeriods = {
          namespace: APIGATEWAY,
          typeKey: 'rates',
          ota: params.otaId,
          los: params.los,
          mealtype: params.mealPlan,
          persons: params.maxPersons,
          roomtype: params.roomType,
          membershiptype: params.membershipType,
          platform: params.platform,
          hotel_id: market.hotelId,
        };

        mapRateParamToAPIParams(params.rateParam, requestPeriods);
        mapCompareParamsToAPIParams(params, requestPeriods);

        extendObj(requestPeriods, requestDays);

        if (!get(model, `expandedRatesummaries.${id}`)) {
          set(
            model,
            'numOfRatesRequestRunning',
            this.numOfRatesRequestRunning + 1,
          );
          set(model, `expandedRatesummaries.${id}`, {
            data: [],
            isLoading: true,
          });
          hash({
            hotel: this.store.findRecord('hotel', market.hotelId, {
              reload: false,
              backgroundReload: false,
            }),
            rates: this.store.query('rate', requestPeriods),
          }).then((hashResult) => {
            const ratesArray = [...hashResult.rates];
            const ratesPerHotelinfo = new Map();
            ratesArray.forEach((rate) => {
              const { hotelinfo, period } = rate;

              if (!ratesPerHotelinfo.has(hotelinfo.id)) {
                ratesPerHotelinfo.set(hotelinfo.id, new Map());
              }

              const ratesPerPeriod = ratesPerHotelinfo.get(hotelinfo.id);
              ratesPerPeriod.set(period.id, rate);
            });

            set(model, `expandedRatesummaries.${id}`, {
              data: ratesPerHotelinfo,
              isLoading: false,
            });

            set(
              model,
              'numOfRatesRequestRunning',
              this.numOfRatesRequestRunning - 1,
            );

            let count = model.counter;
            count += 1;
            set(model, 'counter', count);
          });
        }
      }
    });
  }

  model(params) {
    // Fetch the current model
    const currentModel = this.modelFor('market.marketrates');
    let model;
    if (currentModel && this.addCompetitorsForRow) {
      model = currentModel;
    } else {
      model = this.resetModel();
    }

    // If day is not passed we make it today in the local time
    // This is needed because we can't set a default day in the correct TZ of the hotel
    const day = params.day
      ? dayjs(params.day, 'YYYY-MM-DD')
      : this.hotelAndMarketService.getLocalTime();

    // Fetch x days
    const dateRangeStart = day;
    const dateRangeEnd = day.add(NUM_OF_DAYS - 1, 'day');

    const requestDays = {
      from_date_range_start: dateRangeStart.format('YYYY-MM-DD'),
      from_date_range_end: dateRangeEnd.format('YYYY-MM-DD'),
    };

    if (this.access.hasEvents) {
      let isCached = true;
      let checkdate = dateRangeStart;
      while (isCached && !checkdate.isAfter(dateRangeEnd)) {
        const date = this.store.peekRecord(
          'day',
          checkdate.format('YYYY-MM-DD'),
        );
        if (!date || !date.isComplete?.rates) {
          isCached = false;
        }
        checkdate = checkdate.add(1, 'day');
      }
      if (!isCached) {
        const requestEvents = {
          namespace: APIGATEWAY,
          typeKey: 'events/accepted',
          from_date_range_start: dateRangeStart.format('YYYY-MM-DD'),
          from_date_range_end: dateRangeEnd.format('YYYY-MM-DD'),
          market_id: this.hotelAndMarketService.hotelOrMarket?.id,
        };

        const eventsRequest = this.store.query('day', requestEvents);

        const requestHolidays = cloneDeep(requestEvents);
        requestHolidays.typeKey = 'holidays';
        const holidaysRequest = this.store.query('day', requestHolidays);

        hash({
          eventsResult: eventsRequest,
          holidaysResult: holidaysRequest,
        }).then((resolvedPromises) => {
          const { eventsResult } = resolvedPromises;

          eventsResult.forEach((x) => set(x, 'isComplete.rates', true));
        });
      }
    }

    if (!this.addCompetitorsForRow) {
      set(model, 'loading', true);

      const requestPeriods = {
        namespace: APIGATEWAY,
        typeKey: 'rates/aggregated',
        ota: params.otaId,
        los: params.los,
        mealtype: params.mealPlan,
        persons: params.maxPersons,
        membershiptype: params.membershipType,
        page: params.page - 1,
        platform: params.platform,
        market_id: this.hotelAndMarketService.hotelOrMarket?.id,
      };

      mapRateParamToAPIParams(params.rateParam, requestPeriods);
      mapCompareParamsToAPIParams(params, requestPeriods);

      if (params.brands.length > 0) {
        requestPeriods.brand = params.brands;
      }
      if (params.managementTypes.length > 0) {
        requestPeriods.management_type = params.managementTypes;
      }

      if (params.roomType !== 'all') {
        requestPeriods.roomtype = params.roomType;
      }

      extendObj(requestPeriods, requestDays);

      this.store.query('ratesummary', requestPeriods).then(
        (content) => {
          /* https://guides.emberjs.com/v2.11.0/models/handling-metadata/ */
          const { meta } = content;
          if (meta) {
            set(
              model,
              'marketOrder',
              meta.payload.markets.map((market) => market.id),
            );
            set(model, 'totalPages', meta.results_num_pages + 1);
            set(model, 'hotelsCount', meta.results_count); // The amount of subscriptions that matches the selected filters
          }
          set(model, 'loading', false);
          set(model, 'ratesummaries', [...model.ratesummaries, ...content]);

          // Update disconnected OTAs - refresh when a new ota gets disconnected
          refreshIfOtaIsDisconnected(
            meta.disconnected_otas,
            this.store,
            this.alerting,
          );

          this.fetchExpandedRows(model, params, requestDays);
        },
        (error) => {
          if (
            error?.errors &&
            error.errors[0] &&
            error.errors[0].code === 'page'
          ) {
            this.router.transitionTo({ queryParams: { page: 1 } });
          } else {
            throw error;
          }
        },
      );
    } else {
      this.fetchExpandedRows(model, params, requestDays);
    }
    set(this, 'addCompetitorsForRow', false);

    return model;
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      // isExiting would be false if only the route's model was changing
      set(controller, 'page', 1);
      set(controller, 'expandedRowIds', []);

      // Clear rate data from store
      this.store.unloadAll('event');
      this.store.unloadAll('holiday');
      // this.store.unloadAll('period');
      this.store.unloadAll('day');
      this.store.unloadAll('rate');
      this.store.unloadAll('ratesummary');
    }
  }

  resetModel() {
    return {
      ratesummaries: [],
      expandedRatesummaries: {},
      numOfRatesRequestRunning: 0,
      counter: 0,
      marketOrder: [],
      totalPages: 1,
    };
  }

  @action
  queryParamsDidChange(addedQP, currentQP, removedQP) {
    if (
      (Object.keys(addedQP).length === 1 &&
        !isEmpty(addedQP.expandedRowIds) &&
        Object.keys(removedQP).length === 0) ||
      (Object.keys(removedQP).length === 1 &&
        !isEmpty(removedQP.expandedRowIds) &&
        Object.keys(addedQP).length === 0)
    ) {
      set(this, 'addCompetitorsForRow', true);
    }
    // TODO: This call to super is within an action, and has to refer to the parent
    // class's actions to be safe. This should be refactored to call a normal method
    // on the parent class. If the parent class has not been converted to native
    // classes, it may need to be refactored as well. See
    // https: //github.com/scalvert/ember-native-class-codemod/blob/master/README.md
    // for more details.
    super.actions.queryParamsDidChange.call(this, ...arguments);
  }
}
